import React from 'react';
import { uniqueId } from 'lodash';
import { FormikInputField, FormikFieldDropdown } from 'Components/FormikComponents';
import { Field, FieldArray, FieldProps, useFormikContext } from 'formik';
import { AVOID_INPUT_PRIORITY, MAX_NUM_OF_VELOCITY_INDEXES } from '@brighterion/utils/constants';
import { Icon, Select } from '@brighterion/br-ui';
import { handleKeyEvent, handleOnClickEvent } from '@brighterion/utils/general';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PARAMETERS_PARAM_TYPES } from 'Common/enums';
import styles from './VelocityConfiguration.css';

type VelocityConfigurationProps = {
    velocityConfigData: VelocityConfig;
    parameterData?: UseGetParametersResponse;
};
const VelocityConfiguration = ({ velocityConfigData, parameterData }: VelocityConfigurationProps) => {
    const {
        isSubmitting,
        handleBlur,
        handleChange,
        values,
        setFieldValue
    } = useFormikContext<ParameterVelocityFormSchema>();
    const { t } = useTranslation();

    const handleAggregationFieldChange = (value: any) => {
        const selectedAggregationFieldType = parameterData?.fieldNameHashmap[value].type;

        let correspondingOutputType;
        if (selectedAggregationFieldType === PARAMETERS_PARAM_TYPES.CURRENCY) {
            correspondingOutputType = PARAMETERS_PARAM_TYPES.CURRENCY;
        } else {
            correspondingOutputType = PARAMETERS_PARAM_TYPES.NUMBER;
        }

        // Set the outputType value in the form
        setFieldValue('type', correspondingOutputType);
    };

    return (
        <div>
            <div className={styles.configurationHeader}>{t('Velocity Configuration')}</div>
            <div data-testid='velocity-configuration-content-container' className={styles.configurationContainer}>
                {values.velocityType.toLowerCase() !== 'count' && (
                    <div>
                        <Field name='aggregationField'>
                            {({ field }: FieldProps) => {
                                return (
                                    <Select
                                        size='sm'
                                        id='velocity-aggregationField-dropdown'
                                        placeholder={t('velocityForm.aggregationFieldPlaceholder')}
                                        label='Aggregation Field'
                                        handleClickCallback={(value) => {
                                            handleAggregationFieldChange(value.toString());
                                            setFieldValue(field.name, value);
                                        }}
                                        selectedItems={field.value ?? []}
                                        disabled={isSubmitting}
                                        aria-disabled={isSubmitting}
                                        className={styles.aggregationFieldDropdown}
                                    >
                                        {velocityConfigData.validAggregationFields.map((af) => {
                                            const parameter = parameterData?.fieldNameHashmap[af];
                                            return (
                                                <option key={uniqueId(af)} value={af}>
                                                    {parameter?.message}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                );
                            }}
                        </Field>
                    </div>
                )}

                <label htmlFor='timePeriod.duration' className={styles.timePeriodDurationLabel}>
                    {t('Time Period')}
                </label>

                <div className={styles.timePeriodContainer}>
                    <FormikInputField
                        id='velocity-timePeriod-duration-inputField'
                        name='timePeriod.duration'
                        dataTestId='velocity-timePeriod-duration-inputField'
                        aria-label='velocity-timePeriod-duration-inputField'
                        className={styles.velocityTimePeriodDurationInputField}
                        type='number'
                        placeholder={t('velocityForm.timePeriodValuePlaceholder')}
                        handleBlur={handleBlur}
                        disabled={isSubmitting}
                        handleChange={handleChange}
                        handleKeyDown={(event: React.KeyboardEvent) => {
                            // only allow numbers
                            return AVOID_INPUT_PRIORITY.includes(event.key) && event.preventDefault();
                        }}
                    />

                    <FormikFieldDropdown
                        name='timePeriod.unit'
                        placeholder={t('velocityForm.timePeriodUnitDropdownPlaceholder')}
                        id='velocity-timePeriod-unit-dropdown'
                        size='sm'
                        className={styles.velocityTimePeriodUnitDropdown}
                        disabled={isSubmitting}
                    >
                        {['Days', 'Hours', 'Minutes', 'Seconds'].map((unit) => {
                            return (
                                <option key={uniqueId(unit)} value={unit}>
                                    {unit}
                                </option>
                            );
                        })}
                    </FormikFieldDropdown>
                </div>

                <FieldArray
                    name='indexes'
                    render={(arrayHelper) => (
                        <div className={styles.velocityIndexesContainer}>
                            {values.indexes.length === 0 ? (
                                <FormikFieldDropdown
                                    name='indexes[0]'
                                    index={0}
                                    label='Indexes'
                                    placeholder={t('velocityForm.indexPlaceholder')}
                                    id='initial-velocity-indexes-dropdown'
                                    size='sm'
                                    className={styles.velocityIndexesDropdown}
                                    disabled={isSubmitting}
                                >
                                    {velocityConfigData.validIndexes.map((vIndex) => {
                                        const parameter = parameterData?.fieldNameHashmap[vIndex];
                                        return (
                                            <option key={uniqueId(vIndex)} value={vIndex}>
                                                {parameter?.message}
                                            </option>
                                        );
                                    })}
                                </FormikFieldDropdown>
                            ) : (
                                values.indexes.map((indexInputField, index) => (
                                    <div key={uniqueId(indexInputField)} className={styles.singleIndexContainer}>
                                        <FormikFieldDropdown
                                            name={`indexes.${index}`}
                                            index={index}
                                            label={index === 0 ? 'Indexes' : undefined}
                                            placeholder={t('velocityForm.indexPlaceholder')}
                                            id='velocity-indexes-dropdown'
                                            size='sm'
                                            className={styles.velocityIndexesDropdown}
                                            disabled={isSubmitting}
                                        >
                                            {velocityConfigData.validIndexes.map((vIndex) => {
                                                const parameter = parameterData?.fieldNameHashmap[vIndex];
                                                return (
                                                    <option key={uniqueId(vIndex)} value={vIndex}>
                                                        {parameter?.message}
                                                    </option>
                                                );
                                            })}
                                        </FormikFieldDropdown>

                                        {index > 0 && (
                                            <Icon
                                                data-testid='delete-index-icon'
                                                aria-disabled={indexInputField === '' && index === 0}
                                                className={clsx(
                                                    styles.removeBtn,
                                                    isSubmitting ||
                                                        (indexInputField === '' &&
                                                            index === 0 &&
                                                            styles.removeBtnDisabled)
                                                )}
                                                name='feature-remove'
                                                size='md'
                                                onClick={handleOnClickEvent(() => {
                                                    arrayHelper.remove(index);
                                                })}
                                                onKeyDown={handleKeyEvent(() => {
                                                    arrayHelper.remove(index);
                                                })}
                                            />
                                        )}
                                    </div>
                                ))
                            )}

                            {values.indexes.length < MAX_NUM_OF_VELOCITY_INDEXES && (
                                <button
                                    data-testid='add-index-btn'
                                    type='button'
                                    tabIndex={0}
                                    className={styles.addVelocityIndexBtn}
                                    onClick={handleOnClickEvent(() =>
                                        arrayHelper.insert(values.indexes.length + 1, '')
                                    )}
                                    onKeyDown={handleKeyEvent(() => arrayHelper.insert(values.indexes.length + 1, ''))}
                                    disabled={values.indexes.length >= MAX_NUM_OF_VELOCITY_INDEXES || isSubmitting}
                                >
                                    <Icon name='add-file' size='md' />
                                    <span>{t('Add Index')}</span>
                                </button>
                            )}
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default VelocityConfiguration;
